import React from 'react';
import { Box, Typography, Paper, Tooltip, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CategoryIcon from '@mui/icons-material/Category';
import { getIconByName } from '../IconSelector';

// Theme colors
const THEME_COLOR = '#009688';
const THEME_COLOR_LIGHT = '#e0f2f1';

// Styled tile container
const TileContainer = styled(Paper)(({ theme, selected }) => ({
  position: 'relative',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  border: selected ? `2px solid ${THEME_COLOR}` : '1px solid #e0e0e0',
  backgroundColor: selected ? THEME_COLOR_LIGHT : '#fff',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    transform: 'translateY(-2px)',
    borderColor: selected ? THEME_COLOR : '#bdbdbd',
  },
}));

// Icon container
const IconBox = styled(Box)(({ color }) => ({
  width: 40,
  height: 40,
  borderRadius: 10,
  backgroundColor: color || THEME_COLOR,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 12,
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
}));

// Selected check icon
const SelectedBadge = styled(Box)({
  position: 'absolute',
  top: 8,
  right: 8,
  color: THEME_COLOR,
});

// Price row component
const PriceRow = ({ label, value, isTotal }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.25,
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        color: isTotal ? '#212121' : '#757575',
        fontWeight: isTotal ? 600 : 400,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: isTotal ? 13 : 11,
        fontWeight: isTotal ? 700 : 500,
        color: isTotal ? THEME_COLOR : '#424242',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const ServiceTile = ({
  service,
  selected = false,
  onClick,
  showPricing = true,
  groupIcon = null,
  groupColor = null,
}) => {
  const {
    id,
    name,
    serviceCharge = 0,
    govtCharge = 0,
    taxOnServiceCharge = 0,
    groupName = '',
  } = service;

  // Calculate total
  const total = Number(serviceCharge) + Number(govtCharge) + Number(taxOnServiceCharge);

  // Get icon component
  const IconComponent = groupIcon ? getIconByName(groupIcon) : CategoryIcon;
  const displayColor = groupColor || THEME_COLOR;

  // Format currency
  const formatCurrency = (amount) => {
    return `AED ${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <TileContainer
      elevation={selected ? 2 : 0}
      selected={selected}
      onClick={() => onClick && onClick(service)}
    >
      {/* Selected indicator */}
      {selected && (
        <SelectedBadge>
          <CheckCircleIcon sx={{ fontSize: 22 }} />
        </SelectedBadge>
      )}

      {/* Icon */}
      <IconBox color={displayColor}>
        <IconComponent sx={{ fontSize: 22, color: '#fff' }} />
      </IconBox>

      {/* Service Name */}
      <Tooltip title={name} placement="top" arrow>
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: '#212121',
            mb: 0.5,
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minHeight: 34,
          }}
        >
          {name}
        </Typography>
      </Tooltip>

      {/* Group name chip (optional) */}
      {groupName && (
        <Chip
          label={groupName}
          size="small"
          sx={{
            fontSize: 10,
            height: 18,
            mb: 1,
            backgroundColor: displayColor + '20',
            color: displayColor,
            fontWeight: 500,
            '& .MuiChip-label': {
              px: 1,
            },
          }}
        />
      )}

      {/* Pricing section */}
      {showPricing && (
        <Box sx={{ mt: 'auto', pt: 1 }}>
          <Box sx={{ borderTop: '1px dashed #e0e0e0', pt: 1 }}>
            {govtCharge > 0 && (
              <PriceRow label="Govt Fee" value={formatCurrency(govtCharge)} />
            )}
            {serviceCharge > 0 && (
              <PriceRow label="Service Fee" value={formatCurrency(serviceCharge)} />
            )}
            {taxOnServiceCharge > 0 && (
              <PriceRow label="VAT" value={formatCurrency(taxOnServiceCharge)} />
            )}
            <Box sx={{ borderTop: '1px solid #e0e0e0', mt: 0.5, pt: 0.5 }}>
              <PriceRow label="Total" value={formatCurrency(total)} isTotal />
            </Box>
          </Box>
        </Box>
      )}
    </TileContainer>
  );
};

export default ServiceTile;
